import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, trackFormSubmission, trackError } from '../utils/analytics';
import { useLanguage } from '../contexts/LanguageContext';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const redirectPath = searchParams.get('redirect');

  useEffect(() => {
    // Make the callback function globally available
    window.handleRecaptchaCallback = (token) => {
      console.log('reCAPTCHA token received:', token);
      setRecaptchaToken(token);
      setError(''); // Clear any previous errors
    };

    // Add error callback for reCAPTCHA
    window.handleRecaptchaError = () => {
      console.error('reCAPTCHA error occurred');
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('localhost');
      
      if (isLocalhost) {
        console.warn('reCAPTCHA failed on localhost - this is expected due to domain restrictions');
        setError(t('reCAPTCHA not available on localhost. This is normal for development.'));
        // For localhost development, we can set a dummy token
        setRecaptchaToken('localhost-bypass-token');
      } else {
        setError(t('reCAPTCHA verification failed. Please refresh the page and try again.'));
      }
    };

    // Function to render reCAPTCHA
    const renderRecaptcha = () => {
      const recaptchaElement = document.getElementById('recaptcha-container');
      if (recaptchaElement && window.grecaptcha && window.grecaptcha.enterprise) {
        try {
          // Check if we're running locally
          const isLocalhost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname.includes('localhost');
          
          if (isLocalhost) {
            console.warn('Running on localhost - reCAPTCHA may not work due to domain restrictions');
            // For localhost, we'll render but add a bypass mechanism
          }
          
          window.grecaptcha.enterprise.render('recaptcha-container', {
            'sitekey': '6LeE5GksAAAAAOjnoUQfQi2Pf3SWEbj4qlhk099A',
            'action': 'LOGIN',
            'callback': 'handleRecaptchaCallback',
            'error-callback': 'handleRecaptchaError'
          });
          console.log('reCAPTCHA rendered successfully');
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    };

    // Initialize reCAPTCHA when component mounts
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(() => {
        console.log('reCAPTCHA Enterprise is ready');
        renderRecaptcha();
      });
    } else {
      // If grecaptcha is not loaded yet, wait for it
      const checkRecaptcha = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(checkRecaptcha);
          window.grecaptcha.enterprise.ready(() => {
            console.log('reCAPTCHA Enterprise is ready (delayed)');
            renderRecaptcha();
          });
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkRecaptcha), 10000);
    }

    // Cleanup function to remove global callback
    return () => {
      if (window.handleRecaptchaCallback) {
        delete window.handleRecaptchaCallback;
      }
    };
  }, [t]);

  // Redirect if already logged in
  if (user) {
    const targetPath = redirectPath || "/dashboard";
    return <Navigate to={targetPath} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Track login attempt
    trackEvent('login_attempt', {
      event_category: 'Authentication',
      event_label: 'Login Form Submission'
    });

    // Check if reCAPTCHA token is present
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
    
    if (!recaptchaToken && !isLocalhost) {
      setError(t('Please complete the reCAPTCHA verification'));
      trackError('reCAPTCHA Missing', 'User attempted login without completing reCAPTCHA', 'login');
      setLoading(false);
      return;
    }

    // For localhost, use a bypass token if no real token is available
    const tokenToSend = recaptchaToken || (isLocalhost ? 'localhost-bypass-token' : '');
    
    if (!tokenToSend) {
      setError(t('Please complete the reCAPTCHA verification'));
      trackError('reCAPTCHA Missing', 'No reCAPTCHA token available', 'login');
      setLoading(false);
      return;
    }

    const result = await login(email, password, tokenToSend);
    
    if (!result.success) {
      setError(result.error);
      trackFormSubmission('login_form', false);
      // Reset reCAPTCHA on error
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        try {
          window.grecaptcha.enterprise.reset();
          setRecaptchaToken('');
        } catch (error) {
          console.error('Error resetting reCAPTCHA:', error);
        }
      }
    } else {
      trackFormSubmission('login_form', true);
      // Redirect to intended path after successful login
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Calorie N Sweat</h1>
        
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('Email Address or Username')}
            </label>
            <input
              type="text"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('Enter email or username')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('Password')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)',
                  fontSize: '16px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px'
                }}
                disabled={loading}
                aria-label={showPassword ? t('Hide password') : t('Show password')}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <div 
              id="recaptcha-container"
              style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '20px' 
              }}
            ></div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading || !recaptchaToken}
          >
            {loading ? t('Signing in...') : t('Sign In')}
          </button>
        </form>

        <div className="text-center mt-20">
          <p style={{ color: 'var(--gray-dark)', fontSize: '14px' }}>
            <a 
              href="/forgot-password" 
              style={{ 
                color: 'var(--primary-blue)', 
                textDecoration: 'none',
                marginBottom: '10px',
                display: 'block'
              }}
            >
              {t('Forgot your password?')}
            </a>
            {t('Contact your administrator for account access')}
          </p>
          
          <div style={{ 
            marginTop: '20px', 
            paddingTop: '15px', 
            borderTop: '1px solid var(--gray-medium)',
            fontSize: '12px'
          }}>
            <p style={{ marginBottom: '8px', color: 'var(--gray-dark)' }}>
              {t('By using CalorienSweat, you agree to our:')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <a 
                href="/privacy-policy" 
                style={{ 
                  color: 'var(--primary-blue)', 
                  textDecoration: 'none'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Privacy Policy')}
              </a>
              <span style={{ color: 'var(--gray-medium)' }}>•</span>
              <a 
                href="/terms-of-service" 
                style={{ 
                  color: 'var(--primary-blue)', 
                  textDecoration: 'none'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Terms of Service')}
              </a>
              <span style={{ color: 'var(--gray-medium)' }}>•</span>
              <a 
                href="/about-us" 
                style={{ 
                  color: 'var(--primary-blue)', 
                  textDecoration: 'none'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('About Us')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
